

import { useState, useEffect, useCallback, useRef } from "react";
import { apiRequest } from "../fetchSetup";

const REVIEWS_API = process.env.REACT_APP_REVIEWS_API;

export default function useOrderFeedbackPrompt(user) {
  const [pendingReview, setPendingReview] = useState(null);
  const [showFlash,     setShowFlash]     = useState(false);
  const [showBanner,    setShowBanner]    = useState(false);
  const timerRef = useRef(null);

  const fetchPending = useCallback(async () => {
    if (!user?.email && !user?.phone) return;

    const params = user.email
      ? `email=${encodeURIComponent(user.email)}`
      : `phone=${encodeURIComponent(user.phone)}`;

    try {
      const res  = await apiRequest(`${REVIEWS_API}/my-latest-pending-review?${params}`);
      const data = await res.json();
      const review = data.pending_review;

      setPendingReview(review);

      if (!review) {
        setShowFlash(false);
        setShowBanner(false);
        return;
      }

      const key           = `${review.type}_${review.reference_id}`;
      const flashShownKey = `feedback_flash_shown_${key}`;    // sessionStorage — resets each login
      const bannerDismKey = `feedback_banner_dismissed_${key}`;

      if (!sessionStorage.getItem(flashShownKey)) {
        timerRef.current = setTimeout(() => {
          setShowFlash(true);
          sessionStorage.setItem(flashShownKey, "1");
        }, 2500);
      }

      setShowBanner(!sessionStorage.getItem(bannerDismKey));
    } catch (err) {
      console.error("Failed to fetch pending review:", err);
    }
  }, [user]);

  useEffect(() => {
    fetchPending();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [fetchPending]);

  const dismissFlash = () => setShowFlash(false);

  const dismissBanner = () => {
    if (pendingReview) {
      const key = `${pendingReview.type}_${pendingReview.reference_id}`;
      sessionStorage.setItem(`feedback_banner_dismissed_${key}`, "1");
    }
    setShowBanner(false);
  };

  // After submitting, hide UI for this session.
  // On next login the backend will check feedbacks array size — if the
  // order still qualifies (e.g. a new order came in) the popup returns.
  const markSubmitted = () => {
    if (pendingReview) {
      const key = `${pendingReview.type}_${pendingReview.reference_id}`;
      // Mark flash + banner dismissed for this session so it doesn't
      // immediately re-show after submit
      sessionStorage.setItem(`feedback_flash_shown_${key}`, "1");
      sessionStorage.setItem(`feedback_banner_dismissed_${key}`, "1");
    }
    setShowFlash(false);
    setShowBanner(false);
    setPendingReview(null);
  };

  return {
    pendingReview, showFlash, showBanner,
    dismissFlash, dismissBanner, markSubmitted,
    refetch: fetchPending,
  };
}